import * as React from "react"
import { cn } from "../../lib/utils"
import { useTheme } from "../../contexts/ThemeContext"

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  size?: 'default' | 'sm' | 'lg' | 'icon'
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size = "default", style, ...props }, ref) => {
    const { colors } = useTheme()

    const variantStyles = (() => {
      switch (variant) {
        case "default":
          return {
            backgroundColor: colors.primary,
            borderColor: colors.primary,
            color: "#FFFFFF",
            boxShadow: `0 12px 28px ${colors.primary}24`,
          }
        case "destructive":
          return {
            backgroundColor: colors.error,
            borderColor: colors.error,
            color: "#FFFFFF",
            boxShadow: `0 12px 28px ${colors.error}20`,
          }
        case "outline":
          return {
            backgroundColor: "transparent",
            borderColor: `${colors.border}CC`,
            color: colors.text,
          }
        case "secondary":
          return {
            backgroundColor: colors.surface,
            borderColor: `${colors.border}A6`,
            color: colors.text,
          }
        case "ghost":
          return {
            backgroundColor: "transparent",
            borderColor: "transparent",
            color: colors.text,
          }
        case "link":
          return {
            backgroundColor: "transparent",
            borderColor: "transparent",
            color: colors.primary,
            textDecoration: "underline",
            textUnderlineOffset: "4px",
            boxShadow: "none",
          }
        default:
          return {}
      }
    })()

    return (
      <button
        className={cn(
          "inline-flex items-center justify-center gap-2 border text-sm font-medium transition-[transform,background-color,border-color,color,box-shadow,opacity] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 motion-reduce:transform-none motion-reduce:transition-none",
          {
            "min-h-11 rounded-2xl px-4 py-2.5": size === "default" && variant !== "link",
            "min-h-10 rounded-xl px-3.5 py-2 text-sm": size === "sm" && variant !== "link",
            "min-h-12 rounded-2xl px-5 py-3 text-base": size === "lg" && variant !== "link",
            "h-11 w-11 rounded-2xl p-0": size === "icon",
            "min-h-0 rounded-lg border-none px-0 py-0": variant === "link",
          },
          variant === "default" && "hover:brightness-[1.03]",
          variant === "destructive" && "hover:brightness-[1.03]",
          variant === "outline" && "hover:bg-black/[0.03] dark:hover:bg-white/[0.04]",
          variant === "secondary" && "hover:bg-black/[0.03] dark:hover:bg-white/[0.04]",
          variant === "ghost" && "hover:bg-black/[0.03] dark:hover:bg-white/[0.04]",
          variant === "link" && "hover:opacity-80",
          className,
        )}
        style={{
          ...variantStyles,
          ...style,
        }}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button }
